import type { DataTableColumn, DataTableRenderCellParams } from '@/components/custom/erp';
import type { PhieuXuatDieuChuyen, PhieuXuatDieuChuyenChiTiet } from '@/types/schemas';
import { formatDate, formatMoney } from '@/lib/formatUtils';

type MasterCellParams = DataTableRenderCellParams<PhieuXuatDieuChuyen>;
type DetailCellParams = DataTableRenderCellParams<PhieuXuatDieuChuyenChiTiet>;

export const getDataTableColumns = (
  handleOpenViewForm: (obj: PhieuXuatDieuChuyen) => void,
  totalAmount?: number | null,
  isTotalLoading?: boolean
): DataTableColumn<PhieuXuatDieuChuyen>[] => [
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 100,
    renderCell: (params: MasterCellParams) => {
      const status = params.value;
      if (status == '5') return 'Xuất kho';
      else if (status == '0') return 'Lập chứng từ';
      else if (status == '3') return 'Chờ duyệt';
      else return 'Hủy';
    }
  },
  {
    field: 'ma_tthddt',
    headerName: 'Trạng thái HĐĐT',
    width: 120,
    renderCell: (params: MasterCellParams) => {
      switch (params.row.ma_tthddt) {
        case '0':
          return 'Không sử dụng';
        case '1':
          return 'Chờ phát hành';
        case '2':
          return 'Chờ ký';
        case '3':
          return 'Xác thực';
        case '4':
          return 'Điều chỉnh';
        case '5':
          return 'Thay thế';
        case '7':
          return 'Thuế từ chối';
        case '8':
          return 'Chờ hủy';
        case '9':
          return 'Hủy';
        case '10':
          return 'Thuế không đồng ý';
        default:
          return '';
      }
    }
  },
  {
    field: 'so_ct',
    headerName: 'Số c/từ',
    width: 150,
    renderCell: (params: MasterCellParams) => {
      return (
        <button
          type='button'
          onClick={() => handleOpenViewForm(params.row)}
          className='cursor-pointer hover:text-blue-500 hover:underline'
        >
          {params.row.so_ct}
        </button>
      );
    }
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày c/từ',
    width: 100,
    renderCell: (params: MasterCellParams) => {
      if (!params.value) return '';
      return formatDate(params.value);
    }
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho xuất',
    width: 100,
    renderCell: (params: MasterCellParams) => params.row.ma_kho_data?.ma_kho || params.row.ma_kho
  },
  {
    field: 'ten_kho',
    headerName: 'Tên kho',
    width: 150,
    renderCell: (params: MasterCellParams) => params.row.ma_kho_data?.ten_kho || params.row.ten_kho
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 200
  },
  {
    field: 'so_buoc',
    headerName: 'Số bước',
    width: 200,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: MasterCellParams) => {
      switch (params.row.so_buoc) {
        case '1':
          return '1. Điều chuyển 1 bước';
        case '2':
          return '2. Điều chuyển 2 bước';
        default:
          return '';
      }
    }
  },
  {
    field: 't_tien',
    headerName: 'Tổng tiền',
    width: 120,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: MasterCellParams) => formatMoney(params.row.t_tien || params.row.t_tien_nt || 0),
    renderSearchInput: params => (
      <div className='flex h-full items-center justify-end gap-2 px-2 text-xs'>
        <span className='font-bold'>&#931;</span>
        <span className='font-semibold text-main'>
          {isTotalLoading ? 'Đang tính...' : typeof totalAmount === 'number' ? formatMoney(totalAmount) : '—'}
        </span>
      </div>
    )
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 80,
    renderCell: (params: MasterCellParams) => params.row.ma_nt_data?.ma_nt || params.row.ma_nt
  },
  {
    field: 't_so_luong',
    headerName: 'Tổng số lượng',
    width: 100,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: MasterCellParams) => formatMoney(params.row.t_so_luong || 0)
  },
  {
    field: 'ma_nk',
    headerName: 'Mã ct',
    width: 80,
    renderCell: (params: MasterCellParams) => params.row.ma_nk_data?.ma_nk || ''
  }
];

export const getInputTableColumns = (): DataTableColumn<PhieuXuatDieuChuyenChiTiet>[] => [
  // Vật tư fields
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_vt_data?.ma_vt
  },
  {
    field: 'ten_vt',
    headerName: 'Tên vật tư',
    width: 200,
    renderCell: (params: DetailCellParams) => params.row.ma_vt_data?.ten_vt
  },
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 80,
    renderCell: (params: DetailCellParams) => params.row.ma_vt_data?.dvt_data?.dvt
  },

  // Kho fields
  {
    field: 'ma_kho',
    headerName: 'Kho xuất',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_kho_data?.ma_kho
  },
  {
    field: 'ma_khon',
    headerName: 'Kho nhận',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_khon_data?.ma_kho
  },

  // Vị trí fields
  {
    field: 'ma_vi_tri',
    headerName: 'Từ vị trí',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_vi_tri_data?.ma_vi_tri
  },
  {
    field: 'ma_vi_trin',
    headerName: 'Đến vị trí',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_vi_trin_data?.ma_vi_tri
  },

  // Số lượng fields
  {
    field: 'sl_ton',
    headerName: 'Tồn',
    width: 100,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: DetailCellParams) => formatMoney(params.row.sl_ton || 0)
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 100,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: DetailCellParams) => formatMoney(params.row.so_luong || 0)
  },
  {
    field: 'px_dd',
    headerName: 'Đích danh',
    width: 100
  },

  // Giá tiền fields
  {
    field: 'gia_nt',
    headerName: 'Giá VND',
    width: 120,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: DetailCellParams) => formatMoney(params.row.gia_nt || 0)
  },
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    width: 120,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: DetailCellParams) => formatMoney(params.row.tien_nt || 0)
  },
  // Tài khoản fields
  {
    field: 'tk_vt',
    headerName: 'TK có',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.tk_vt_data?.code
  },
  {
    field: 'tk_du',
    headerName: 'Tk nợ',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.tk_du_data?.code
  },

  // Lý do và ghi chú
  {
    field: 'ma_nx',
    headerName: 'Lý do đ/c',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_nx_data?.ma_nx
  },
  {
    field: 'ghi_chu',
    headerName: 'Ghi chú/Tên hàng hoá HĐĐT',
    width: 200
  },

  // Bộ phận và vụ việc
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_vv_data?.ma_vu_viec
  },

  // Hợp đồng và thanh toán
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_dtt_data?.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_ku_data?.ma_ku
  },

  // Phí và sản phẩm
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_phi_data?.ma_phi
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_sp_data?.ma_vt
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 120,
    renderCell: (params: DetailCellParams) => params.row.ma_lsx_data?.ma_lsx
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 140,
    renderCell: (params: DetailCellParams) => params.row.ma_cp0_data?.ma_cpkhl
  },

  // Reference fields
  {
    field: 'so_ct_pn',
    headerName: 'Số CT PN',
    width: 120
  }
];

export const historyColumns: DataTableColumn[] = [
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
];
